/**
 * navOrder — urutan grup + SUB-FITUR menu sidebar (2026-06-13, user request).
 *
 * Sumber kebenaran:
 *  - Default global : GET/PUT/DELETE /settings/nav-order (super admin, disimpan
 *    di system_settings key="nav_order_default").
 *  - Override personal: /user-prefs/display/nav_order (endpoint generik).
 * Resolve: personal > global default > urutan bawaan kode (NAV_GROUPS).
 * localStorage `ens_nav_order` = cache { personal, global } untuk paint instan.
 *
 * Shape payload: { groups: [groupKey,...], items: { groupKey: ["/route",...] } }.
 * Backward compat: payload array polos / { order: [...] } = grup-only.
 */
import { LayoutDashboard, Inbox, Bell, FolderKanban, Users, TrendingUp, CalendarCheck, Package, PieChart, Landmark, ShieldCheck, Settings, FileClock } from "lucide-react";
import api from "@/lib/api";

export const NAV_GROUPS = [
  {
    label: "Ringkasan",
    key: "overview",
    flat: true,
    items: [
      { to: "/dashboard", label: "Dasbor", icon: LayoutDashboard, testid: "nav-dashboard", feature: "dashboard" },
    ],
  },
  {
    label: "Tugas & Tim",
    key: "task-team",
    items: [
      { to: "/my-work", label: "Pekerjaan Saya", icon: Inbox, testid: "nav-my-work", badgeSource: "my_work" },
      { to: "/info-agenda", label: "Informasi & Agenda", icon: Bell, testid: "nav-info-agenda", feature: "reminders" },
    ],
  },
  {
    label: "Proyek",
    key: "projects",
    items: [
      { to: "/projects", label: "Manajemen Proyek", icon: FolderKanban, testid: "nav-projects", feature: "projects" },
    ],
  },
  {
    // R232 (2026-10-20, user request): grup "SDM & Data Perusahaan" → "SDM".
    // Menu "Cuti & Izin" DIGABUNG ke "Kehadiran & Perizinan" (tab Cuti & Izin
    // di halaman kehadiran); /leaves lama di-redirect (App.js). Item Kehadiran
    // memakai `features` (tampil bila user punya akses attendance ATAU leaves).
    label: "SDM",
    key: "sdm-data",
    items: [
      { to: "/team", label: "Karyawan & Tim", icon: Users, testid: "nav-team", feature: "team" },
      { to: "/performance", label: "Kinerja", icon: TrendingUp, testid: "nav-performance", feature: "performance" },
      { to: "/attendance", label: "Kehadiran & Perizinan", icon: CalendarCheck, testid: "nav-attendance", features: ["attendance", "leaves"] },
    ],
  },
  {
    // R232: Data Aset KELUAR dari kelompok SDM → grup baru. Aset yang dipinjam
    // karyawan tetap terlihat di profil karyawan (lihat ProfileDialog).
    label: "Operasional & Aset",
    key: "operasional-aset",
    items: [
      { to: "/assets", label: "Data Aset", icon: Package, testid: "nav-assets", feature: "assets" },
    ],
  },
  {
    label: "Keuangan",
    key: "keuangan",
    items: [
      { to: "/finance-report", label: "Laporan Keuangan", icon: PieChart, testid: "nav-finance-report", feature: "finance_report" },
      // 2026-06-20 (user request): Weekly Petty Cash pindah ke dalam fitur "Kas & Bank".
      { to: "/kas-bank", label: "Kas & Bank", icon: Landmark, testid: "nav-kas-bank", feature: "petty_cash" },
    ],
  },
  {
    label: "Administrasi",
    key: "administrasi",
    items: [
      { to: "/admin/users", label: "Manajemen Pengguna", icon: ShieldCheck, testid: "nav-admin-users", feature: "user_management" },
      { to: "/settings", label: "Pengaturan", icon: Settings, testid: "nav-settings", feature: "settings" },
      { to: "/admin/audit", label: "Audit & Aktivitas", icon: FileClock, testid: "nav-admin-audit", feature: "personnel_manage" },
    ],
  },
];

const LS_KEY = "ens_nav_order";
const GROUP_KEY_RE = /^[a-z0-9_-]{1,64}$/;
const ITEM_KEY_RE = /^\/[a-z0-9_/-]{0,63}$/;

/** Urutan bawaan kode — grup + semua sub-fitur tiap grup. */
export function defaultNavOrder() {
  return {
    groups: NAV_GROUPS.map((g) => g.key),
    items: Object.fromEntries(NAV_GROUPS.map((g) => [g.key, g.items.map((n) => n.to)])),
  };
}

export function defaultNavKeys() {
  return NAV_GROUPS.map((g) => g.key);
}

export function readNavOrderCache() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {}; }
  catch { return {}; }
}

function _cleanGroups(v) {
  if (!Array.isArray(v)) return null;
  const out = v.filter((k) => typeof k === "string" && GROUP_KEY_RE.test(k));
  return out.length ? out : null;
}

function _cleanItems(v) {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const out = {};
  for (const [gk, lst] of Object.entries(v)) {
    if (!GROUP_KEY_RE.test(gk) || !Array.isArray(lst)) continue;
    out[gk] = lst.filter((it) => typeof it === "string" && ITEM_KEY_RE.test(it));
  }
  return out;
}

/** Normalisasi payload tersimpan → { groups, items } | null. */
function _normalize(raw) {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    const g = _cleanGroups(raw);
    return g ? { groups: g, items: {} } : null;
  }
  if (typeof raw === "object") {
    const g = _cleanGroups(raw.groups) || _cleanGroups(raw.order);
    if (!g) return null;
    return { groups: g, items: _cleanItems(raw.items) };
  }
  return null;
}

function _patchCache(patch) {
  try {
    const cur = readNavOrderCache();
    localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch { /* noop */ }
}

/** Ambil override personal + default global dari server, update cache. */
export async function loadNavOrder() {
  try {
    const [p, g] = await Promise.all([
      api.get("/user-prefs/display/nav_order").catch(() => ({ data: {} })),
      api.get("/settings/nav-order").catch(() => ({ data: {} })),
    ]);
    const res = { personal: _normalize(p.data), global: _normalize(g.data) };
    _patchCache(res);
    return res;
  } catch {
    const c = readNavOrderCache();
    return { personal: _normalize(c.personal), global: _normalize(c.global) };
  }
}

/** Resolve urutan final: personal > global > bawaan. Key tak dikenal dibuang;
 *  grup/route baru (belum ada di order) ditaruh di akhir. */
export function resolveNavOrder(res) {
  const def = defaultNavOrder();
  const src = _normalize(res?.personal) || _normalize(res?.global);
  if (!src) return def;
  const knownGroups = new Set(def.groups);
  const groups = src.groups.filter((k) => knownGroups.has(k));
  for (const k of def.groups) if (!groups.includes(k)) groups.push(k);
  const defaultItems = def.items;
  const items = {};
  for (const gk of groups) {
    const known = defaultItems[gk] || [];
    const wanted = (src.items?.[gk] || []).filter((t) => known.includes(t));
    for (const t of known) if (!wanted.includes(t)) wanted.push(t);
    items[gk] = wanted;
  }
  return { groups, items };
}

/** Urutkan array grup + sub-fitur di dalamnya sesuai resolved order
 *  (stable untuk key tak dikenal). */
export function orderNavGroups(groups, resolved) {
  if (!resolved?.groups?.length) return groups;
  const gIdx = new Map(resolved.groups.map((k, i) => [k, i]));
  return [...groups]
    .sort((a, b) => {
      const ia = gIdx.has(a.key) ? gIdx.get(a.key) : resolved.groups.length;
      const ib = gIdx.has(b.key) ? gIdx.get(b.key) : resolved.groups.length;
      return ia - ib;
    })
    .map((g) => {
      const ord = resolved.items?.[g.key];
      if (!ord?.length) return g;
      const iIdx = new Map(ord.map((t, i) => [t, i]));
      return {
        ...g,
        items: [...g.items].sort((a, b) => {
          const ia = iIdx.has(a.to) ? iIdx.get(a.to) : ord.length;
          const ib = iIdx.has(b.to) ? iIdx.get(b.to) : ord.length;
          return ia - ib;
        }),
      };
    });
}

export async function savePersonalNavOrder(order) {
  await api.put("/user-prefs/display/nav_order", order);
  _patchCache({ personal: order });
}

export async function resetPersonalNavOrder() {
  await api.delete("/user-prefs/display/nav_order");
  _patchCache({ personal: null });
}

export async function saveGlobalNavOrder(order) {
  await api.put("/settings/nav-order", order);
  _patchCache({ global: order });
}

export async function resetGlobalNavOrder() {
  await api.delete("/settings/nav-order");
  _patchCache({ global: null });
}

/** Beri tahu AppShell agar memuat ulang urutan tanpa refresh halaman. */
export function notifyNavOrderChanged() {
  try { window.dispatchEvent(new Event("ens:nav-order-changed")); } catch { /* noop */ }
}
